import axios from 'axios';
import logger from '../core/logger/logger';
import errorHandler from '../core/error/errorHandler';
import errorNotification from '../core/error/errorNotification';
import { isLegalAcceptanceRequired, LEGAL_REQUIRED_EVENT } from '../features/legal/utils/legalEvents';
import i18n, { currentLanguage } from '../i18n';

// Базовый URL API
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev-api.datum-games.com';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.datum-games.com';

// Создаем экземпляр axios с базовой конфигурацией
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 секунд timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Кэш токена в памяти: Telegram WebView может сбросить localStorage
// в первые секунды первой (холодной) сессии, при этом сам токен ещё валиден.
// Используем кэш как fallback, чтобы не терять авторизацию.
let inMemoryAccessToken = null;

// Интерцептор для добавления токена авторизации
apiClient.interceptors.request.use(
  (config) => {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      inMemoryAccessToken = storedToken;
    }
    const token = storedToken || inMemoryAccessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Язык интерфейса: бэк переводит на него тексты ошибок (поле message) и инвойсы Stars
    config.headers['Accept-Language'] = currentLanguage();
    
    // Логируем запрос в debug режиме
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }
    
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ответов
apiClient.interceptors.response.use(
  (response) => {
    // Логируем успешный ответ в debug режиме
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};

    // Обработка 401/403 ошибки с автоматическим refresh token
    // Бэкенд может вернуть 401 (AuthenticationException отловлен в BearerTokenAuthenticationFilter)
    // либо 403 — когда ExpiredJwtException пролетает мимо фильтра и Spring Security
    // бьёт AccessDeniedException для анонимного контекста. Оба статуса трактуем
    // как «токен протух», если у нас локально есть accessToken.
    const status = error.response?.status;
    const url = originalRequest.url || '';

    // 403 «примите документы» — не протухший токен: без refresh и alert, App откроет окно согласия
    if (isLegalAcceptanceRequired(error)) {
      window.dispatchEvent(new CustomEvent(LEGAL_REQUIRED_EVENT));
      return Promise.reject(error);
    }
    const isAuthError = status === 401 || status === 403;
    // Не рефрешим сам refresh/login — иначе словим бесконечный цикл.
    const isAuthEndpoint = url.includes('/v1/auth/refresh') || url.includes('/v1/auth/telegram');

    // Восстанавливаемся даже если accessToken отсутствует в localStorage
    // (сброс хранилища Telegram WebView): refreshAccessToken() при отсутствии
    // refresh-токена сам переавторизуется через Telegram initData.
    if (isAuthError && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Пытаемся обновить токен
        const newToken = await errorHandler.refreshAccessToken();

        // Обновляем заголовок авторизации
        inMemoryAccessToken = newToken;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        logger.info('🔄 Retrying request with new token', {
          url: originalRequest.url,
          method: originalRequest.method,
          originalStatus: status,
        });

        // Повторяем запрос с новым токеном
        return apiClient(originalRequest);
      } catch (refreshError) {
        logger.error('Failed to refresh token', refreshError);
        
        // Если не удалось обновить токен, очищаем данные
        inMemoryAccessToken = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
        
        // Показываем уведомление пользователю о необходимости обновить страницу
        // Для 401 ошибки показываем специальное сообщение
        errorNotification.showApiError(error);
        
        // Выбрасываем специальную ошибку авторизации
        const authError = new Error(i18n.t('error.session_expired_refresh', 'Session expired. Please refresh the page.'));
        authError.isAuthError = true;
        authError.response = error.response; // Сохраняем оригинальный response для совместимости
        return Promise.reject(authError);
      }
    }

    // Обработка других ошибок
    const errorInfo = errorHandler.handleError(error, error.config);
    
    // Если ошибка повторяемая и не было попыток, пытаемся повторить
    if (errorInfo.shouldRetry && !originalRequest._retry && !originalRequest._isRetrying) {
      originalRequest._isRetrying = true;
      
      try {
        return await errorHandler.retryRequest(() => apiClient(originalRequest));
      } catch (retryError) {
        originalRequest._isRetrying = false;
        
        // Показываем уведомление при финальной ошибке (кроме запросов с config.silentError)
        if (originalRequest.silentError) {
          // вызывающий код сам покажет мягкую подсказку
        } else if (retryError.response) {
          errorNotification.showApiError(retryError);
        } else {
          errorNotification.showNetworkError(retryError);
        }
        
        return Promise.reject(retryError);
      }
    }
    
    // Показываем уведомление для критических ошибок (не повторяемых)
    // Для повторяемых ошибок показываем только после всех попыток
    // config.silentError: true — запрос мини-игры / доски, где 422 = обычный «недопустимый ход»,
    // вызывающий код показывает мягкую подсказку сам (без Telegram alert)
    if (!errorInfo.shouldRetry && !error.config?.silentError) {
      if (error.response) {
        errorNotification.showApiError(error);
      } else if (!error.request) {
        // Это не сетевая ошибка и не API ошибка - возможно, ошибка кода
        logger.error('Unexpected error', error);
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
